import { useQuery as useReactQuery } from 'react-query';

import { getFetcher } from 'src/core/api/fetcher';

import { reshapeApprovalScheme } from './reshape-approval-scheme';
import { type Member } from './useMembersQuery';
import { useCompanyId } from '../../app/hooks/useCompanyId';
import {
  type ApprovalSchemeApi,
  type ApprovalRight,
  type ApprovalRule,
  type ApprovalStep,
  type ApprovalScheme,
} from '../../company/structure/approval-flows';

type ApprovalRightWithMemberName = ApprovalRight & {
  approverName?: string;
};

type ApprovalStepWithMemberNames = Omit<ApprovalStep, 'rights'> & {
  rights: ApprovalRightWithMemberName[];
};

type ApprovalRuleWithMemberNames = Omit<ApprovalRule, 'steps'> & {
  steps: ApprovalStepWithMemberNames[];
};

export type ApprovalSchemeWithMemberNames = Omit<ApprovalScheme, 'rules'> & {
  rules: ApprovalRuleWithMemberNames[];
};

export const APPROVAL_SCHEMES_QUERY_KEY = [
  'approval-schemes',
  { includeAll: true },
] as const;

export const useApprovalSchemesQuery = () => {
  const companyId = useCompanyId();
  const membersQuery = useMembersQueryRaw();
  return useReactQuery({
    queryKey: APPROVAL_SCHEMES_QUERY_KEY,
    enabled: membersQuery.isSuccess,
    select: (data: ApprovalSchemeApi[]) => {
      if (!membersQuery.data) {
        return [];
      }
      return populateApprovalSchemesWithMemberNames(
        data.map(reshapeApprovalScheme),
        membersQuery.data,
      );
    },
    queryFn: getFetcher<ApprovalSchemeApi[]>({
      companyId,
      getRequest: () => ({
        type: 'rest',
        target: 'companyAPI',
        endpoint: `/approval-schemes?includeAll=true`,
        method: 'get',
      }),
    }),
  });
};

const useMembersQueryRaw = () => {
  const companyId = useCompanyId();
  return useReactQuery({
    queryKey: ['members'],
    queryFn: getFetcher<Member[]>({
      companyId,
      getRequest: () => ({
        type: 'rest',
        target: 'companyAPI',
        endpoint: `/users?includePending=true`,
        method: 'get',
      }),
    }),
  });
};

const populateApprovalSchemesWithMemberNames = (
  approvalSchemes: ApprovalScheme[],
  members: Member[],
): ApprovalSchemeWithMemberNames[] => {
  const memberNameById = new Map(
    members.map((member) => [member.id, member.fullname]),
  );

  const enrichRightWithName = (
    right: ApprovalRight,
  ): ApprovalRightWithMemberName => ({
    ...right,
    approverName:
      right.approverType === 'user'
        ? memberNameById.get(right.approverId)
        : undefined,
  });

  return approvalSchemes.map((scheme) => ({
    ...scheme,
    rules: scheme.rules.map((rule) => ({
      ...rule,
      steps: rule.steps.map((step) => ({
        ...step,
        rights: step.rights.map(enrichRightWithName),
      })),
    })),
  }));
};
